<template>
  <v-main>
    <v-container fluid class="pa-6">
      <!-- Greeting -->
      <div class="pw-hero mb-6">
        <div>
          <h1 class="pw-hero__title">{{ greeting }}, {{ displayName }}</h1>
          <p class="pw-hero__sub mb-0">
            Here is everything you can manage from this panel.
          </p>
        </div>
        <v-chip small class="pw-hero__role" color="primary" outlined>
          {{ roleLabel }}
        </v-chip>
      </div>

      <!-- Renewals that need a call -->
      <div v-if="actions.toCall > 0" class="pw-action-strip mb-6">
        <v-icon color="warning darken-1">mdi-alert-outline</v-icon>
        <div class="pw-action-strip__text">
          <strong>{{ actions.toCall }} {{ actions.toCall === 1 ? "customer needs" : "customers need" }} a renewal call</strong>
          <span v-if="actions.expiring7"> &middot; {{ actions.expiring7 }} plan{{ actions.expiring7 === 1 ? "" : "s" }} expire in the next 7 days</span>
          <span v-if="actions.expired"> &middot; {{ actions.expired }} expired without renewal</span>
        </div>
        <v-btn depressed small color="warning" to="/action-dashboard">
          <v-icon left small>mdi-lightning-bolt</v-icon>Open Action Dashboard
        </v-btn>
      </div>

      <!-- Quick access -->
      <div v-for="section in visibleSections" :key="section.title" class="mb-7">
        <div class="pw-section-label">{{ section.title }}</div>
        <p class="pw-section-desc">{{ section.description }}</p>

        <v-row dense>
          <v-col
            v-for="link in section.links"
            :key="link.to"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card :to="link.to" class="pw-tile" flat>
              <div class="pw-tile__icon">
                <v-icon size="22" color="primary">{{ link.icon }}</v-icon>
              </div>
              <div class="pw-tile__body">
                <div class="pw-tile__name">{{ link.name }}</div>
                <div class="pw-tile__desc">{{ link.desc }}</div>
              </div>
              <v-icon size="18" class="pw-tile__chevron"
                >mdi-chevron-right</v-icon
              >
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </v-main>
</template>

<script>
// Quick-access tiles for every screen the signed-in role can open. This is
// the home page for roles without the admin dashboard, and the "Shortcut
// Dashboard" page for those with it.
import axios from "axios";

export default {
  name: "ShortcutsPage",
  data() {
    return {
      // Counts for the renewal strip (Action Dashboard)
      actions: { toCall: 0, expiring7: 0, expired: 0 },
    };
  },
  async mounted() {
    if (!this.canAccess("action_dashboard")) return;
    try {
      const { data } = await axios.get("reports/action_dashboard", { params: { summary: 1 } });
      const first = (key, i = 0) => {
        const tile = data.data.tiles.find((t) => t.key === key);
        return tile && tile.summary[i] ? Number(tile.summary[i].value) || 0 : 0;
      };
      this.actions = {
        toCall: first("action_follow_up"),
        expiring7: first("action_expiring_7", 1),
        expired: first("action_expired_not_renewed"),
      };
    } catch (e) {
      // The strip is a hint; the page works without it
    }
  },
  computed: {
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return "Good morning";
      if (hour < 17) return "Good afternoon";
      return "Good evening";
    },
    displayName() {
      return this.user.first_name || this.user.user_name || this.user.email;
    },
    roleLabel() {
      if (
        (this.roleName === "BACKEND ADMIN" && this.company.id != null) ||
        this.roleName === "ADMIN"
      ) {
        return "Admin";
      }
      return this.positionName || this.roleName;
    },
    sections() {
      return [
        {
          title: "Reports",
          description:
            "Performance, customers, walks, revenue, commission and finance.",
          links: [
            {
              name: "Action Dashboard",
              desc: "Plans ending or ended without renewal: who to call",
              icon: "mdi-lightning-bolt-outline",
              to: "/action-dashboard",
              module: "action_dashboard",
            },
            {
              name: "All Reports",
              desc: "Every report available to you, with export",
              icon: "mdi-file-chart-outline",
              to: "/reports",
              module: "reports",
            },
          ],
        },
        {
          title: "Bookings",
          description: "Day-to-day walk scheduling and customer requests.",
          links: [
            {
              name: "Monthly Bookings",
              desc: "Walks booked by customers each month",
              icon: "mdi-calendar-month-outline",
              to: "/user-monthly-job-requests",
              module: "monthly_bookings",
            },
            {
              name: "Walk Reschedule Requests",
              desc: "Customers asking to move a booked walk",
              icon: "mdi-calendar-refresh-outline",
              to: "/customer-walk-requests",
              module: "walk_reschedule_requests",
            },
            {
              name: "Staff Leave Requests",
              desc: "Time-off requests from your walkers",
              icon: "mdi-calendar-remove-outline",
              to: "/leave-requests",
              module: "leave_requests",
            },
          ],
        },
        {
          title: "People & Network",
          description:
            "Customers, franchise partners and the staff on the ground.",
          links: [
            {
              name: "Users",
              desc: "Everyone with an account in the system",
              icon: "mdi-account-group-outline",
              to: "/users",
              module: "users",
            },
            {
              name: "Master Franchises",
              desc: "Regional partners who own territories",
              icon: "mdi-crown-outline",
              to: "/master-franchisees",
              module: "master_franchisees",
            },
            {
              name: "Franchises",
              desc: "Local branches delivering the service",
              icon: "mdi-storefront-outline",
              to: "/franchisees",
              module: "franchisees",
            },
            {
              name: "Franchise Staff",
              desc: "Walkers and trainers employed by a branch",
              icon: "mdi-account-tie-outline",
              to: "/franchisee-staffs",
              module: "franchisee_staffs",
            },
          ],
        },
        {
          title: "Applications",
          description:
            "People applying to work with or partner with Pet Walkies.",
          links: [
            {
              name: "Job Applications",
              desc: "Applications from the careers page",
              icon: "mdi-briefcase-account-outline",
              to: "/career-applications",
              module: "career_applications",
            },
            {
              name: "Dog Walkers",
              desc: "Sign-ups to become a walker",
              icon: "mdi-dog-side",
              to: "/pet-walkie-career-requests",
              module: "dog_walker_requests",
            },
            {
              name: "Pet Trainers",
              desc: "Sign-ups to become a trainer",
              icon: "mdi-whistle-outline",
              to: "/pet-trainer-career-requests",
              module: "pet_trainer_requests",
            },
            {
              name: "Daycare Partners",
              desc: "Sign-ups to host pet daycare",
              icon: "mdi-home-heart",
              to: "/daycare-career-requests",
              module: "daycare_requests",
            },
            {
              name: "Franchise Owners",
              desc: "Enquiries to open a new franchise",
              icon: "mdi-store-plus-outline",
              to: "/franchisee-career-requests",
              module: "franchise_owner_requests",
            },
          ],
        },
        {
          title: "Website Content",
          description: "What customers see on the app and website.",
          links: [
            {
              name: "Blog Posts",
              desc: "Articles published on the website",
              icon: "mdi-post-outline",
              to: "/blogs",
              module: "blogs",
            },
            {
              name: "App Banners",
              desc: "Promotional images shown in the app",
              icon: "mdi-image-multiple-outline",
              to: "/banners",
              module: "banners",
            },
            {
              name: "Upcoming Services",
              desc: "Services you are announcing soon",
              icon: "mdi-star-outline",
              to: "/upcoming-services",
              module: "upcoming_services",
            },
            {
              name: "Contact Enquiries",
              desc: "Messages sent through the contact form",
              icon: "mdi-email-outline",
              to: "/contact-messages",
              module: "contact_messages",
            },
          ],
        },
        {
          title: "Setup",
          description:
            "Pricing, offers and the options the rest of the app uses.",
          links: [
            {
              name: "Subscription Plans",
              desc: "Walk packages and their pricing",
              icon: "mdi-package-variant-closed",
              to: "/plans",
              module: "plans",
            },
            {
              name: "Discount Coupons",
              desc: "Promo codes customers can redeem",
              icon: "mdi-ticket-percent-outline",
              to: "/coupons",
              module: "coupons",
            },
            {
              name: "Reward Settings",
              desc: "Referral and loyalty reward rules",
              icon: "mdi-gift-outline",
              to: "/reward-settings",
              module: "reward_settings",
            },
            {
              name: "Roles & Permissions",
              desc: "Who is allowed to do what",
              icon: "mdi-shield-key-outline",
              to: "/permissions",
              module: "permissions",
            },
            {
              name: "Job Positions",
              desc: "Staff designations used across branches",
              icon: "mdi-badge-account-outline",
              to: "/positions",
              module: "positions",
            },
            {
              name: "State Holidays",
              desc: "Non-working days by state",
              icon: "mdi-calendar-star",
              to: "/state-holidays",
              module: "state_holidays",
            },
            {
              name: "State GST Rates",
              desc: "Tax percentages applied per state",
              icon: "mdi-percent-outline",
              to: "/state-gsts",
              module: "state_gsts",
            },
            {
              name: "Master Categories",
              desc: "Dropdown groups used in forms",
              icon: "mdi-shape-outline",
              to: "/values",
              module: "values",
            },
          ],
        },
      ];
    },
    // A tile shows when the user's position has its module (Job Positions)
    visibleSections() {
      return this.sections
        .map((section) => ({
          ...section,
          links: section.links.filter((link) => this.canAccess(link.module)),
        }))
        .filter((section) => section.links.length > 0);
    },
  },
};
</script>

<style scoped>
.pw-action-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff8e6;
  border: 1px solid #f6dfa0;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 13px;
  color: #5c4a12;
}

.pw-action-strip__text {
  flex: 1 1 auto;
}

.pw-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.pw-hero__title {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1a2436;
  line-height: 1.25;
}

.pw-hero__sub {
  font-size: 0.875rem;
  color: #66738a;
  margin-top: 4px;
}

.pw-hero__role {
  font-weight: 600;
}

.pw-section-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #8d99ad;
}

.pw-section-desc {
  font-size: 0.8125rem;
  color: #66738a;
  margin: 2px 0 12px;
}

/* Tile */
.pw-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px;
  height: 100%;
  border: 1px solid #e4e8ee !important;
  border-radius: 14px !important;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;
}

.pw-tile:hover {
  border-color: #c3d3ec !important;
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.07) !important;
  transform: translateY(-1px);
}

.pw-tile__icon {
  flex: 0 0 40px;
  height: 40px;
  width: 40px;
  border-radius: 11px;
  background: #eef3fb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pw-tile__body {
  min-width: 0;
  flex: 1 1 auto;
}

.pw-tile__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a2436;
  line-height: 1.3;
}

.pw-tile__desc {
  font-size: 0.75rem;
  color: #66738a;
  line-height: 1.35;
  margin-top: 2px;
}

.pw-tile__chevron {
  color: #c2cbd8;
  flex: 0 0 auto;
}

.pw-tile:hover .pw-tile__chevron {
  color: var(--v-primary-base);
}
</style>
